import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styles from './folder-list.module.scss';

import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { SVGS } from '@zonofi/zono-money-design-system';
import {
  ImageItem,
  S3File,
  getImageBlobFromLambda,
  toImagesFormat,
} from './hooks/use-folder-list';
import { FullscreenPreview } from '../fullscreen-preview/fullscreen-preview';

interface FolderListProps {
  folders: { id: string; name: string; date: string; items: number }[];
  images: Record<string, ImageItem[]>;
  files: S3File[];
}

type ZoomPanState = {
  scale: number;
  translate: { x: number; y: number };
};

export const FolderList: React.FC<FolderListProps> = ({
  folders,
  images,
  files,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [shareLoadingId, setShareLoadingId] = useState<string | null>(null);
  const [imageZoomPan, setImageZoomPan] = useState<
    Record<string, ZoomPanState>
  >({});
  const imageZoomPanRef = useRef<Record<string, ZoomPanState>>({});
  const createDefaultZoomPan = () => ({
    scale: 1,
    translate: { x: 0, y: 0 },
  });

  const [searchParams] = useSearchParams(); // <-- add support for query string

  // Example: convert query params to an object
  const obj = Object.fromEntries(searchParams.entries());
  const workspaceId = obj.id;
  const domainName =
    typeof window !== 'undefined'
      ? window.location.hostname
      : (location?.pathname ?? '');
  const isQa = domainName.includes('-qa') || !domainName.includes('mobile');

  const imageFiles = useMemo(
    () =>
      (files || []).filter((file) => {
        const fileName = file?.name?.toLowerCase?.() ?? '';
        const path = file?.path?.toLowerCase?.() ?? '';
        return !fileName.endsWith('.csv') && !path.endsWith('.csv');
      }),
    [files]
  );

  const fileImages = useMemo(
    () => toImagesFormat(imageFiles, {})?.filter(Boolean) ?? [],
    [imageFiles]
  );

  const filteredFolders = useMemo(
    () =>
      query?.length > 1
        ? folders.filter((f) =>
            f.name.toLowerCase().includes(query.trim().toLowerCase())
          )
        : folders,
    [query, folders]
  );

  const totalItems = useMemo(
    () =>
      filteredFolders.reduce((sum, f) => {
        const list = (images as any)[f.id] as
          | Array<{ src: string }>
          | undefined;
        return sum + (list ? list.length : 0);
      }, 0) + fileImages.length,
    [filteredFolders, fileImages]
  );

  useEffect(() => {
    if (!fileImages.length) return;
    setImageZoomPan((prev) => {
      let mutated = false;
      const next = { ...prev };
      fileImages.forEach((img) => {
        if (!img?.src || next[img.src]) return;
        const defaultState = createDefaultZoomPan();
        next[img.src] = defaultState;
        imageZoomPanRef.current[img.src] = defaultState;
        mutated = true;
      });
      return mutated ? next : prev;
    });
  }, [fileImages]);

  const updateZoomPanForImage = useCallback(
    (src: string, updater: (current: ZoomPanState) => ZoomPanState) => {
      setImageZoomPan((prev) => {
        const current = prev[src] || createDefaultZoomPan();
        const updated = updater(current);
        imageZoomPanRef.current[src] = updated;
        if (
          current.scale === updated.scale &&
          current.translate.x === updated.translate.x &&
          current.translate.y === updated.translate.y
        ) {
          return prev;
        }
        return {
          ...prev,
          [src]: updated,
        };
      });
    },
    []
  );

  const handleToggleSelect = useCallback((src: string) => {
    setSelected((prev) => {
      if (prev.includes(src)) {
        return prev.filter((item) => item !== src);
      }
      return [...prev, src];
    });
  }, []);

  const openPreview = useCallback((startIndex: number) => {
    setActiveIndex(startIndex >= 0 ? startIndex : 0);
    setIsPreviewOpen(true);
  }, []);

  const shareImageToWhatsapp = useCallback(
    async (imagesToShare: ImageItem[]) => {
      const s3Urls = imagesToShare.map((img) => img.src);
      const blobs = await Promise.all(
        s3Urls.map((url) => getImageBlobFromLambda(url, isQa))
      );
      const filesWithLinks = blobs.map((blob, index) => {
        const extension = blob.type?.split('/')[1] || 'jpg';
        const filename = `${imagesToShare[index].id || index}.${extension}`;
        const file = new File([blob], filename, {
          type: blob.type || 'image/jpeg',
        });
        return {
          file,
          name: imagesToShare[index].name || '',
          linkUrl: imagesToShare[index].linkUrl || '',
        };
      });

      const preparedFiles = filesWithLinks.map((item) => item.file);
      const shareText =
        filesWithLinks.length === 1
          ? `${filesWithLinks?.[0]?.name}: ${filesWithLinks?.[0]?.linkUrl}`
          : filesWithLinks
              .filter((item) => item.linkUrl)
              .map((item) => `${item.name}: ${item.linkUrl}`)
              .join('\n');

      await navigator.share({
        files: preparedFiles,
        text: `Check out these products:\n${shareText}`,
        title: `Check out these products:\n${shareText}`,
      });
    },
    [isQa]
  );

  const handleShare = useCallback(
    async (image: ImageItem) => {
      if (!image?.src || shareLoadingId === image.src) return;
      setShareLoadingId(image.src);
      try {
        await shareImageToWhatsapp([image]);
      } catch (err) {
        console.error('Share failed:', err);
      } finally {
        setShareLoadingId((prev) => (prev === image.src ? null : prev));
      }
    },
    [shareImageToWhatsapp, shareLoadingId]
  );

  const fileFolderMeta = useMemo(
    () => ({
      id: 'files',
      name: 'Files',
      date: '',
      items: fileImages.length,
    }),
    [fileImages]
  );

  return (
    <div className="catalog-tool">
      <div className={styles.folderList}>
        {!isPreviewOpen && (
          <>
            <div className={styles.headerBar}>
              <div className={styles.headerLeft}>
                <div className={styles.appBadge}>
                  <img
                    src={SVGS.Merchandising}
                    alt="merchandising"
                    style={{ width: '60%', height: '100%' }}
                  />
                </div>

                <div>
                  <div className={styles.title}>Merchandising</div>
                  <div className={styles.counts}>
                    {filteredFolders.length} folders • {totalItems} items
                  </div>
                </div>
              </div>
              <div className={styles.searchWrapper}>
                {isSearchOpen ? (
                  <>
                    <SearchRoundedIcon className={styles.searchIcon} />
                    <input
                      className={styles.searchInput}
                      type="text"
                      value={query}
                      placeholder="Search"
                      autoFocus
                      onChange={(e) => setQuery(e.target.value)}
                      onBlur={() => {
                        if (!query) setIsSearchOpen(false);
                      }}
                    />
                  </>
                ) : (
                  <button
                    className={styles.searchButton}
                    onClick={() => setIsSearchOpen(true)}
                    aria-label="Open search"
                  >
                    <SearchRoundedIcon />
                  </button>
                )}
              </div>
            </div>

            {filteredFolders.map((f) => (
              <div
                key={f.name}
                className={styles.folderCard}
                onClick={() =>
                  navigate(`/catalog-tool/${f.id}?id=${workspaceId}`)
                }
              >
                <div className={styles.row}>
                  <div className={styles.folderIconWrap}>
                    <img
                      src={SVGS.FolderIcon}
                      alt="folder"
                      className={styles.folderIcon}
                    />
                  </div>
                  <div className={styles.folderContent}>
                    <div className={styles.folderHeader}>
                      <span>{f.name}</span>
                    </div>
                    <div className={styles.folderMeta}>
                      <p>
                        {f.date} • {Math.max(0, f.items)} items
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {fileImages.length > 0 && (
              <div className={styles.fileSection}>
                {/* <div className={styles.fileSectionHeader}>
                  Files ({fileImages.length})
                </div> */}
                <div className={styles.fileList} role="list">
                  {fileImages.map((img, index) => (
                    <div
                      key={img.src}
                      className={styles.fileCard}
                      role="listitem"
                      onClick={() => openPreview(index)}
                    >
                      <div className={styles.fileInfoWrap}>
                        <div className={styles.fileThumb}>
                          <img
                            src={img.src}
                            alt={img.name || 'file'}
                            loading="lazy"
                          />
                        </div>
                        <div className={styles.fileInfo}>
                          <div className={styles.fileName}>
                            {img.name || 'Untitled'}
                          </div>
                          {img.linkUrl ? (
                            <div className={styles.fileLink}>{img.linkUrl}</div>
                          ) : null}
                        </div>
                      </div>

                      {shareLoadingId === img.src ? (
                        <span style={{ fontFamily: 'Segoe-UI-VF' }}>
                          Preparing...
                        </span>
                      ) : (
                        <img
                          src={SVGS.ForwardIcon}
                          alt="forward"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShare(img);
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <FullscreenPreview
          imgs={fileImages}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          imageZoomPan={imageZoomPan}
          isPreviewOpen={isPreviewOpen}
          setIsPreviewOpen={setIsPreviewOpen}
          styles={styles}
          selected={selected}
          folderMeta={fileFolderMeta}
          handleToggleSelect={handleToggleSelect}
          updateZoomPanForImage={updateZoomPanForImage}
        />
      </div>
    </div>
  );
};
